import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import * as prismicH from '@prismicio/helpers';
import { SliceZone } from '@prismicio/react';
import Head from 'next/head';
import { useContext, useEffect } from 'react';

import Image from '@/contents/General/Image';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import { createClient } from '@/prismicio';
import { components } from '@/slices';
import * as stylesTheme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import { Page } from '@/types/domain';
import type { SlicesFetched } from '@/types/main';
import type { ThemeDocument } from '@/types/prismic.generated';
import FetchSlices from '@/utils/FetchSlices';
import { getBlankImage } from '@/utils/GetBlankString';
import getRevalidateSec from '@/utils/getRevalidateSec';

const HeaderImage = styled(Box)`
  display: flex;
  flex-direction: column;
  position: relative;
  width: inherit;
  max-width: inherit;
  height: 100vw;
  max-height: 506px;
  img {
    object-fit: cover;
    height: inherit;
    max-height: inherit;
  }
`;

const Content = styled(Box)`
  position: relative;
  z-index: 1;
`;

type Props = {
  theme: ThemeDocument;
  slices: SlicesFetched;
  path: string;
};

const Index: NextPageWithLayout<Props> = ({ theme: page, slices, path }) => {
  const { setPage } = useContext(GlobalContext);

  useEffect(() => {
    setPage(Page.THEME);
  }, []);

  return (
    <>
      <Head>
        <title>{`${page.data.title}でおすすめのキャンプ場ガイド - iihi`}</title>
        <meta property="og:url" content={`https://iihi.life/theme/${path}`} />
        <meta
          name="description"
          content={`${page.data.meta_description ?? ''}`}
        />
        <meta
          property="og:title"
          content={`${page.data.title}でおすすめのキャンプ場ガイド - iihi`}
        />
        <meta
          property="og:description"
          content={`${page.data.meta_description ?? ''}`}
        />
        <meta
          property="og:image"
          content={`${prismicH.asImageSrc(page.data.og_image) ?? ''}`}
        />
        <link
          href={page.data.top_image.url ?? getBlankImage()}
          as="image"
          rel="preload"
        />
      </Head>
      <HeaderImage>
        <Image
          src={page.data.top_image.url ?? getBlankImage()}
          width="100%"
          height="100%"
          alt={page.data.title ?? ''}
        />
      </HeaderImage>
      <Stack
        column
        space={3}
        sx={{
          p: `${stylesTheme.default.spacing(3)} ${stylesTheme.default.spacing(
            3
          )} 0`,
        }}
      >
        <Typography fontSize={28} fontWeight={700} variant="h1">
          {page.data.title}
        </Typography>
        <Typography fontSize={15}>{page.data.description}</Typography>
      </Stack>
      <Content>
        <Box
          sx={{
            paddingTop: stylesTheme.default.spacing(5),
            paddingBottom: stylesTheme.default.spacing(4),
          }}
        >
          <SliceZone slices={slices} components={components} />
        </Box>
      </Content>
    </>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;

export const getStaticPaths = async () => {
  if (process.env.BUILD_WITH_GENERATION === 'true') {
    const client = createClient();
    const result = await client.getAllByType('theme');
    return {
      paths: result.map((item) => ({ params: { area: item.uid } })),
      fallback: 'blocking',
    };
  }
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export async function getStaticProps({ params, previewData }: any) {
  const client = createClient({ previewData });

  const result = await client.getByUID('theme', params.area);
  if (!result) {
    return {
      notFound: true,
    };
  }
  const { area: path } = params;
  const theme: ThemeDocument = result;
  const slices = await FetchSlices(theme.data.slices, client);

  return {
    props: { theme, slices, path },
    revalidate: getRevalidateSec(),
  };
}
